import { createContext, useContext, useEffect, useState } from "react";
import { Formik, Form, FieldArray } from "formik";
import { Accordion, Spinner } from "react-bootstrap";
import { getHeaderData, updateHeaderData } from "../../api/header";
import { toast } from "react-toastify";
import Label from "../../components/label";
import { FormikInput } from "../../components/input";
import ImageUploader from "../../components/upload-image";
import Button from "../../components/Buttons";
import { Trash2, Plus } from "lucide-react";

// context
const PageContext = createContext();
const usePageProvider = () => useContext(PageContext);

const PageProvider = ({ children }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = async () => {
    try {
      setLoading(true);
      const res = await getHeaderData();
      setData(res || {
        logo: { image: "", link: "/" },
        left_links: [],
        right_links: [],
        contact_number: { label: "", value: "" }
      });
    } catch (error) {
      toast.error("Failed to load header settings");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleSubmit = async (values) => {
    try {
      await updateHeaderData(values);
      toast.success("Header settings updated successfully");
      fetchData();
    } catch (error) {
      toast.error("Failed to update settings");
    }
  };

  return (
    <PageContext.Provider value={{ initialValues: data, loading, handleSubmit }}>
      {children}
    </PageContext.Provider>
  );
};

const HeaderContent = () => {
  const { initialValues, loading, handleSubmit } = usePageProvider();

  if (loading) {
    return (
      <div className="d-flex justify-content-center py-5">
        <Spinner animation="border" variant="primary" />
      </div>
    );
  }

  return (
    <div>
      <div className="my-4">
        <h2 className="fw-bold mb-1 text-[#1a1a4e]">Header Settings</h2>
        <p className="text-muted small mb-0">Manage website navigation links and logo</p>
      </div>

      <Formik
        initialValues={initialValues}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {({ values, setFieldValue }) => (
          <Form>
            <Accordion defaultActiveKey="0" className="mb-4">
              {/* LOGO SECTION */}
              <Accordion.Item eventKey="0">
                <Accordion.Header>Logo Settings</Accordion.Header>
                <Accordion.Body>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label label="Center Logo" className="mb-2" />
                      <div className="border rounded-xl p-3 bg-light/50">
                        <ImageUploader
                          imgStyle={{ width: "100px", height: "100px", borderRadius: "8px", objectFit: 'contain' }}
                          imageUrl={values.logo?.image}
                          onChange={(res) => setFieldValue("logo.image", res?.data?.file_url)}
                        />
                      </div>
                    </div>
                    <div>
                      <Label label="Logo Home Link" className="mb-1" />
                      <FormikInput name="logo.link" placeholder="/" />
                    </div>
                  </div>
                </Accordion.Body>
              </Accordion.Item>

              <Accordion.Item eventKey="1">
                <Accordion.Header>Navigation Links (Left & Right)</Accordion.Header>
                <Accordion.Body>
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                    <div>
                      <Label label="Left Section Links" className="mb-3 font-bold text-[#1a1a4e]" />
                      <FieldArray name="left_links">
                        {(arrayHelpers) => (
                          <div className="space-y-3">
                            {values.left_links.map((_, index) => (
                              <div key={index} className="flex gap-2 items-start bg-gray-50 p-3 rounded-xl border">
                                <div className="flex-1 grid grid-cols-2 gap-2">
                                  <FormikInput name={`left_links.${index}.label`} placeholder="Label" />
                                  <FormikInput name={`left_links.${index}.href`} placeholder="URL" />
                                </div>
                                <button
                                  type="button"
                                  className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                                  onClick={() => arrayHelpers.remove(index)}
                                >
                                  <Trash2 size={18} />
                                </button>
                              </div>
                            ))}
                            <Button
                              type="button"
                              variant="outline"
                              className="w-full !border-dashed py-2"
                              onClick={() => arrayHelpers.push({ label: "", href: "" })}
                            >
                              <Plus size={18} className="me-2" /> Add Left Link
                            </Button>
                          </div>
                        )}
                      </FieldArray>
                    </div>

                    <div>
                      <Label label="Right Section Links" className="mb-3 font-bold text-[#1a1a4e]" />
                      <FieldArray name="right_links">
                        {(arrayHelpers) => (
                          <div className="space-y-3">
                            {values.right_links.map((_, index) => (
                              <div key={index} className="flex gap-2 items-start bg-gray-50 p-3 rounded-xl border">
                                <div className="flex-1 grid grid-cols-2 gap-2">
                                  <FormikInput name={`right_links.${index}.label`} placeholder="Label" />
                                  <FormikInput name={`right_links.${index}.href`} placeholder="URL" />
                                </div>
                                <button
                                  type="button"
                                  className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                                  onClick={() => arrayHelpers.remove(index)}
                                >
                                  <Trash2 size={18} />
                                </button>
                              </div>
                            ))}
                            <Button
                              type="button"
                              variant="outline"
                              className="w-full !border-dashed py-2"
                              onClick={() => arrayHelpers.push({ label: "", href: "" })}
                            >
                              <Plus size={18} className="me-2" /> Add Right Link
                            </Button>
                          </div>
                        )}
                      </FieldArray>
                    </div>
                  </div>
                </Accordion.Body>
              </Accordion.Item>

              <Accordion.Item eventKey="2">
                <Accordion.Header>Contact Information</Accordion.Header>
                <Accordion.Body>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label label="Contact Label / Number" className="mb-1" />
                      <FormikInput name="contact_number.label" placeholder="e.g. 1234567890" />
                    </div>
                    <div>
                      <Label label="Contact URL (tel: / mailto:)" className="mb-1" />
                      <FormikInput name="contact_number.value" placeholder="e.g. tel:1234567890" />
                    </div>
                  </div>
                </Accordion.Body>
              </Accordion.Item>
            </Accordion>

            <div className="save-button-bar !bg-white/95 !backdrop-blur-md">
              <Button type="submit" >SAVE HEADER SETTINGS</Button>
            </div>
          </Form>
        )}
      </Formik>
    </div>
  );
};

const HeaderSettings = () => (
  <PageProvider>
    <HeaderContent />
  </PageProvider>
);

export default HeaderSettings;
